import { trackRegisterInAffiliate } from "~/app/analytics/ga/affiliate";
import { useTranslation } from "react-i18next";
import { selectSavedAffiliate, selectVisitedAffiliate } from "~/domains/affiliate/redux/selectors";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { Button } from "~/common/mui";
import { type SalesPartnerSearchFormValues, trackPartnerFinderSearch } from "~/app/analytics/ga/salesPartner";
import type { GoogleMarker } from "~/domains/salesPartner/types/SalesPartnerTypes";
import { transformSelectedMarkerData } from "~/features/cart/B2CRequestPage/utils";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";

type Props = {
  open?: boolean;
  onClose?: () => void;
  selectedMarker: GoogleMarker | undefined;
  searchValues: SalesPartnerSearchFormValues;
};
export const RegisterToSaveSnackbar = (props: Props) => {
  const { t } = useTranslation();

  const { openLogin, component, openRegister } = useLoginPopup();

  const savedAffiliatePartner = useSelector(selectSavedAffiliate);
  const visitedAffiliatePartner = useSelector(selectVisitedAffiliate);

  const onClickRegister = () => {
    trackSignInOrRegister(true);
    const bannerName = savedAffiliatePartner?.name ?? visitedAffiliatePartner?.name ?? "";
    trackRegisterInAffiliate(bannerName);
    openRegister();
  };

  const trackSignInOrRegister = (isRegister: boolean) => {
    trackPartnerFinderSearch(
      props.searchValues,
      isRegister ? "Register Intended" : "Sign In Intended",
      "Click",
      transformSelectedMarkerData(t, props.selectedMarker),
    );
  };

  return (
    <>
      <Snackbar
        {...props}
        onClose={props.onClose}
        message={t("page.salespartner.snackbar.register.label")}
        action={
          <>
            <Button
              color="inversePrimary"
              variant={"text"}
              size="medium"
              onClick={() => {
                trackSignInOrRegister(false);
                openLogin();
              }}
            >
              {t("header.link.signIn")}
            </Button>
            <em> {t("common.or")} </em>
            <Button color="inversePrimary" variant={"text"} size="medium" onClick={onClickRegister}>
              {t("header.link.register")}
            </Button>
            <SnackbarCloseButton onClick={props.onClose} />
          </>
        }
      />
      {component}
    </>
  );
};

interface UseLoginProps {
  alternativeGAAction?: string;
}

const useLoginPopup = (props?: UseLoginProps) => {
  const dispatch = useDispatch();
  const openLogin = () => dispatch(openAuthOverlay("login", { alternativeGAAction: props?.alternativeGAAction }));
  const openRegister = () => dispatch(openAuthOverlay("register"));
  return {
    openLogin,
    openRegister,
    component: null,
  };
};
